import { createContext, useContext, useEffect, useState } from "react";
import client from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=unauth, obj=auth
  const [users, setUsers] = useState([]);

  const loadUsers = async () => {
    try {
      const { data } = await client.get("/users");
      setUsers(data);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    client
      .get("/auth/me")
      .then(({ data }) => {
        setUser(data);
        loadUsers();
      })
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    const h = () => refreshMe();
    window.addEventListener("auth:refresh", h);
    return () => window.removeEventListener("auth:refresh", h);
  }, []);

  // Returns { mfaRequired, challenge } when a second factor is needed.
  const login = async (email, password) => {
    const { data } = await client.post("/auth/login", { email, password });
    if (data.mfa_required) return { mfaRequired: true, challenge: data.challenge_token };
    setUser(data.user);
    loadUsers();
    return { mfaRequired: false, user: data.user };
  };

  const loginMfa = async (challenge, code, remember = false) => {
    const { data } = await client.post("/auth/login/mfa", { challenge_token: challenge, code, remember });
    setUser(data.user);
    loadUsers();
    return data.user;
  };

  const loginPasskey = async () => {
    const { startAuthentication } = await import("@simplewebauthn/browser");
    const { data: opts } = await client.post("/auth/passkeys/login/options");
    const credential = await startAuthentication({ optionsJSON: opts.options });
    const { data } = await client.post("/auth/passkeys/login/verify", { ceremony: opts.ceremony, credential });
    setUser(data.user);
    loadUsers();
    return data.user;
  };

  const refreshMe = async () => {
    try {
      const { data } = await client.get("/auth/me");
      setUser(data);
    } catch (e) {
      /* ignore */
    }
  };

  const patchPreferences = (prefs) => {
    setUser((u) => (u ? { ...u, preferences: { ...(u.preferences || {}), ...prefs } } : u));
  };

  const logout = async () => {
    try {
      await client.post("/auth/logout");
    } catch (e) {
      /* ignore */
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, users, login, loginMfa, loginPasskey, logout, loadUsers, refreshMe, patchPreferences }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
